/**
 * Trusted Types Configuration for DOM XSS Prevention
 * ============================================================================
 * 
 * This module configures Trusted Types policies to prevent DOM-based XSS attacks
 * by ensuring all HTML strings are sanitized before being inserted into the DOM.
 * 
 * Key Features:
 * - Default policy for automatic sanitization of dangerous sinks
 * - DOMPurify integration for robust HTML sanitization
 * - Type-safe wrappers for common DOM operations
 * - Fallback support for browsers without Trusted Types
 * 
 * Usage:
 * ```typescript
 * import { sanitizeHTML, createTrustedHTML } from './lib/trustedTypes';
 * 
 * // Sanitize HTML string
 * const clean = sanitizeHTML(userInput);
 * element.innerHTML = clean;
 * 
 * // Create trusted HTML for React
 * <div dangerouslySetInnerHTML={{ __html: createTrustedHTML(content) }} />
 * ```
 */

import DOMPurify from 'dompurify';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Trusted Types API declarations
 * These types match the W3C Trusted Types specification
 */
declare global {
  interface Window {
    trustedTypes?: {
      createPolicy: (
        name: string,
        policy: {
          createHTML?: (input: string) => string;
          createScript?: (input: string) => string;
          createScriptURL?: (input: string) => string;
        }
      ) => TrustedTypePolicy;
      defaultPolicy?: TrustedTypePolicy;
    };
  }

  interface TrustedTypePolicy {
    name: string;
    createHTML(input: string): TrustedHTML;
    createScript?(input: string): TrustedScript;
    createScriptURL?(input: string): TrustedScriptURL;
  }

  interface TrustedHTML {
    toString(): string;
  }

  interface TrustedScript {
    toString(): string;
  }

  interface TrustedScriptURL {
    toString(): string;
  }
}

// Standalone type alias — avoids "not defined" errors in function signatures
type InsertPosition = 'beforebegin' | 'afterbegin' | 'beforeend' | 'afterend';

// ============================================================================
// DOMPURIFY CONFIGURATION
// ============================================================================

/**
 * Configure DOMPurify with secure defaults
 * - Allow common HTML tags and attributes
 * - Remove dangerous elements (script, iframe, object, embed)
 * - Sanitize URLs in href and src attributes
 */
const DOMPURIFY_CONFIG = {
  ALLOWED_TAGS: [
    'a', 'abbr', 'b', 'blockquote', 'br', 'code', 'div', 'em', 'h1', 'h2', 'h3',
    'h4', 'h5', 'h6', 'hr', 'i', 'img', 'li', 'ol', 'p', 'pre', 'span', 'strong',
    'sub', 'sup', 'table', 'tbody', 'td', 'th', 'thead', 'tr', 'ul',
  ],
  ALLOWED_ATTR: [
    'class', 'href', 'id', 'src', 'alt', 'title', 'target', 'rel',
    'aria-label', 'aria-hidden', 'role',
  ],
  ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  KEEP_CONTENT: true,
  RETURN_TRUSTED_TYPE: false, // We'll wrap it ourselves
};

/**
 * Sanitize HTML string using DOMPurify
 * This is the core sanitization function used by all Trusted Types policies
 */
function sanitizeWithDOMPurify(dirty: string): string {
  return DOMPurify.sanitize(dirty, DOMPURIFY_CONFIG);
}

// ============================================================================
// TRUSTED TYPES POLICY CREATION
// ============================================================================

let defaultPolicy: TrustedTypePolicy | null = null;
let isInitialized = false;

/**
 * Initialize Trusted Types policies
 * Creates a default policy that automatically sanitizes all HTML
 * 
 * This function is idempotent - calling it multiple times is safe
 */
export function initializeTrustedTypes(): void {
  if (isInitialized) return;
  isInitialized = true;

  // Check if Trusted Types is supported
  if (!window.trustedTypes) {
    // ✅ Only warn in development — production stays silent
    if (import.meta.env.DEV) {
      console.warn(
        '[Trusted Types] Browser does not support Trusted Types API. ' +
        'HTML sanitization will still occur, but without browser-level enforcement.'
      );
    }
    return;
  }

  try {
    // Create default policy for automatic sanitization
    // This policy is invoked automatically when dangerous sinks are used
    defaultPolicy = window.trustedTypes.createPolicy('default', {
      createHTML: (input: string) => {
        const sanitized = sanitizeWithDOMPurify(input);
        
        // Log in development if content was modified
        if (import.meta.env.DEV && sanitized !== input) {
          console.warn(
            '[Trusted Types] HTML was sanitized by default policy:',
            { original: input.substring(0, 100), sanitized: sanitized.substring(0, 100) }
          );
        }
        
        return sanitized;
      },
    });

    // ✅ Only log success in development
    if (import.meta.env.DEV) {
      console.info('[Trusted Types] Default policy initialized successfully');
    }
  } catch (error) {
    // Policy might already exist (e.g., in HMR during development)
    if (error instanceof Error && error.name === 'TypeError') {
      // ✅ Only warn in development
      if (import.meta.env.DEV) {
        console.warn('[Trusted Types] Default policy already exists');
      }
    } else {
      // ✅ Only log errors in development
      if (import.meta.env.DEV) {
        console.error('[Trusted Types] Failed to create default policy:', error);
      }
    }
  }
}

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Sanitize HTML string and return safe HTML
 * 
 * This function always sanitizes the input, regardless of Trusted Types support.
 * In browsers with Trusted Types, it returns a TrustedHTML object.
 * In other browsers, it returns a sanitized string.
 * 
 * @param dirty - Untrusted HTML string
 * @returns Sanitized HTML (TrustedHTML or string)
 * 
 * @example
 * ```typescript
 * const userInput = '<script>alert("xss")</script><p>Hello</p>';
 * const safe = sanitizeHTML(userInput);
 * element.innerHTML = safe; // Only "<p>Hello</p>" is inserted
 * ```
 */
export function sanitizeHTML(dirty: string): string | TrustedHTML {
  const sanitized = sanitizeWithDOMPurify(dirty);

  // If Trusted Types is supported and we have a policy, create TrustedHTML
  if (window.trustedTypes && defaultPolicy) {
    try {
      return defaultPolicy.createHTML(sanitized);
    } catch (error) {
      // ✅ Only log errors in development
      if (import.meta.env.DEV) {
        console.error('[Trusted Types] Failed to create TrustedHTML:', error);
      }
      return sanitized;
    }
  }

  // Fallback: return sanitized string
  return sanitized;
}

/**
 * Create trusted HTML for use in React's dangerouslySetInnerHTML
 * 
 * @param dirty - Untrusted HTML string
 * @returns Sanitized HTML string (always a string for React compatibility)
 * 
 * @example
 * ```tsx
 * <div dangerouslySetInnerHTML={{ __html: createTrustedHTML(content) }} />
 * ```
 */
export function createTrustedHTML(dirty: string): string {
  const result = sanitizeHTML(dirty);
  // Convert TrustedHTML to string if needed
  return typeof result === 'string' ? result : result.toString();
}

/**
 * Safely set innerHTML on an element
 * 
 * @param element - Target DOM element
 * @param html - Untrusted HTML string
 * 
 * @example
 * ```typescript
 * const div = document.createElement('div');
 * setInnerHTML(div, userGeneratedContent);
 * ```
 */
export function setInnerHTML(element: Element, html: string): void {
  element.innerHTML = sanitizeHTML(html) as string;
}

/**
 * Safely insert HTML adjacent to an element
 * 
 * @param element - Target DOM element
 * @param position - Where to insert the HTML
 * @param html - Untrusted HTML string
 * 
 * @example
 * ```typescript
 * insertAdjacentHTML(element, 'beforeend', '<p>New content</p>');
 * ```
 */
export function insertAdjacentHTML(
  element: Element,
  position: InsertPosition,
  html: string
): void {
  element.insertAdjacentHTML(position, sanitizeHTML(html) as string);
}

/**
 * Check if Trusted Types is supported and initialized
 * 
 * @returns true if Trusted Types API is available
 */
export function isTrustedTypesSupported(): boolean {
  return typeof window.trustedTypes !== 'undefined';
}

/**
 * Get the current Trusted Types enforcement status
 * 
 * @returns Object with support and enforcement status
 */
export function getTrustedTypesStatus(): {
  supported: boolean;
  enforced: boolean;
  policyCount: number;
} {
  const supported = isTrustedTypesSupported();
  
  return {
    supported,
    enforced: supported && defaultPolicy !== null,
    policyCount: supported ? 1 : 0,
  };
}

// ============================================================================
// NOTE: initializeTrustedTypes() is called explicitly from src/main.tsx.
// Auto-initialization on import has been removed to avoid side effects
// and to keep module loading predictable.
// ============================================================================